import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

/**
 * Hook que resolve o país do usuário corrente como ISO 3166-1 alpha-2.
 *
 * Ordem de resolução (Release D — monetização internacional):
 *   1. `user.countryCode` (do backend, persistido em users.country_code).
 *   2. Heurística client-side via `Intl.DateTimeFormat().resolvedOptions().timeZone`
 *      mapeada pros 50+ países cobertos pelo `CountryFilter`. Pra users
 *      anônimos ou recém-cadastrados antes do backfill V127.
 *   3. `'BR'` default — retrocompat com sistema atual.
 *
 * Usado por:
 *   - `SubscriptionPage.tsx` pra decidir gateway (BR→Asaas / ≠BR→Stripe).
 *   - `MyAccount.tsx` pra exibir dropdown de país com valor inicial.
 *   - Lookup de preço regional em `premium_plan_prices`.
 *
 * Quando o backend não devolve countryCode (user pré-V127 ou intl
 * anônimo), o backend lê `CloudFront-Viewer-Country` no header — então
 * essa heurística client-side é só fallback de UX, não fonte canônica.
 */
export function useCountry(): { country: string; setCountryOverride: (c: string) => void } {
  const { user } = useAuth();
  const [override, setOverride] = useState<string | null>(null);

  // Heurística por timezone — usada só quando user não tem countryCode.
  // Mapa parcial (cobre ~30 timezones mais comuns). Países fora caem em 'BR'.
  const [tzGuess, setTzGuess] = useState<string>('BR');
  useEffect(() => {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      setTzGuess(timezoneToCountry(tz) ?? 'BR');
    } catch {
      setTzGuess('BR');
    }
  }, []);

  // Override via query param `?country=US` — útil pra testes manuais de
  // pricing regional sem alterar o country_code do user no banco. Tem
  // precedência sobre user.countryCode (caso 1). Exposed em produção
  // porque o impacto é só visual no client; o checkout ainda valida no
  // backend via CountryFilter (que prioriza header CloudFront).
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const queryCountry = params.get('country');
      if (queryCountry && /^[A-Za-z]{2}$/.test(queryCountry)) {
        setOverride(queryCountry.toUpperCase());
      }
    } catch { /* ignore */ }
  }, []);

  const country =
    override ??
    (user?.countryCode && user.countryCode.length === 2 ? user.countryCode : null) ??
    tzGuess;

  return { country: country.toUpperCase(), setCountryOverride: setOverride };
}

/**
 * Mapa parcial timezone → país ISO 3166-1 alpha-2. Cobre os timezones
 * mais comuns dos países alvos do Bolão Elite. Retorna null se desconhecido
 * — caller cai no default 'BR'.
 *
 * Não é exaustivo (existem 400+ timezones IANA); ampliar conforme demanda.
 */
function timezoneToCountry(tz: string): string | null {
  const TZ_MAP: Record<string, string> = {
    // Brasil
    'America/Sao_Paulo': 'BR',
    'America/Bahia': 'BR',
    'America/Fortaleza': 'BR',
    'America/Recife': 'BR',
    'America/Belem': 'BR',
    'America/Manaus': 'BR',
    'America/Cuiaba': 'BR',
    'America/Campo_Grande': 'BR',
    'America/Porto_Velho': 'BR',
    'America/Rio_Branco': 'BR',
    'America/Noronha': 'BR',
    // América do Norte
    'America/New_York': 'US',
    'America/Chicago': 'US',
    'America/Denver': 'US',
    'America/Los_Angeles': 'US',
    'America/Phoenix': 'US',
    'America/Anchorage': 'US',
    'America/Honolulu': 'US',
    'America/Toronto': 'CA',
    'America/Vancouver': 'CA',
    'America/Edmonton': 'CA',
    'America/Mexico_City': 'MX',
    'America/Cancun': 'MX',
    // América Latina
    'America/Argentina/Buenos_Aires': 'AR',
    'America/Santiago': 'CL',
    'America/Bogota': 'CO',
    'America/Lima': 'PE',
    'America/Montevideo': 'UY',
    'America/Asuncion': 'PY',
    'America/La_Paz': 'BO',
    'America/Guayaquil': 'EC',
    'America/Caracas': 'VE',
    // Europa Ocidental
    'Europe/London': 'GB',
    'Europe/Berlin': 'DE',
    'Europe/Paris': 'FR',
    'Europe/Madrid': 'ES',
    'Europe/Rome': 'IT',
    'Europe/Lisbon': 'PT',
    'Europe/Amsterdam': 'NL',
    'Europe/Brussels': 'BE',
    'Europe/Vienna': 'AT',
    'Europe/Dublin': 'IE',
    'Europe/Helsinki': 'FI',
    'Europe/Stockholm': 'SE',
    'Europe/Copenhagen': 'DK',
    'Europe/Oslo': 'NO',
    'Europe/Zurich': 'CH',
    'Europe/Warsaw': 'PL',
    'Europe/Prague': 'CZ',
    'Europe/Athens': 'GR',
    // Ásia / Oceania
    'Asia/Tokyo': 'JP',
    'Asia/Seoul': 'KR',
    'Asia/Shanghai': 'CN',
    'Asia/Hong_Kong': 'HK',
    'Asia/Singapore': 'SG',
    'Asia/Taipei': 'TW',
    'Asia/Kolkata': 'IN',
    'Asia/Bangkok': 'TH',
    'Asia/Ho_Chi_Minh': 'VN',
    'Asia/Jakarta': 'ID',
    'Asia/Kuala_Lumpur': 'MY',
    'Asia/Manila': 'PH',
    'Australia/Sydney': 'AU',
    'Australia/Melbourne': 'AU',
    'Australia/Perth': 'AU',
    'Pacific/Auckland': 'NZ',
    // Outros
    'Asia/Jerusalem': 'IL',
    'Asia/Dubai': 'AE',
    'Asia/Riyadh': 'SA',
    'Europe/Istanbul': 'TR',
    'Africa/Johannesburg': 'ZA',
  };
  return TZ_MAP[tz] ?? null;
}
